import { Injectable } from "@angular/core";
import { ModalService } from "./ui/modal.service";
import { MixpanelService, TEventName } from "src/app/services/mixpanel.service";
import { NursesFacade } from "./facades/nurses/nurses.facade";
import { filter, take } from "rxjs";
import { Router } from "@angular/router";
import { NurseService } from "./api/nurse.service";
import { IModalData, TTriggerSource } from "src/app/types/modal-data";
import { UserModel } from "src/app/models/user";
import { MessagesService } from "./messages.service";
import { markAsTrustedSuccess } from "../state/actions/nurses/nurses.actions";

type ModalType = 'invite-to-bs' | 'mark-nurse-as-trusted';
interface IFacilityNurseEventPayload  {
  facility: string;
  nurse: UserModel;
  facilityName: string;
  triggerSource: TTriggerSource;
}

@Injectable({
  providedIn: 'root',
})
export class ModalBuilderService {
  constructor(
    private modalService: ModalService,
    private mixpanel: MixpanelService,
    private nurseFacade: NursesFacade,
    private router: Router,
    private nurseService: NurseService, 
    private messagesService: MessagesService
  ) { }

  showBSPopup(payload: IFacilityNurseEventPayload) {
    const { nurse, facility, triggerSource} = payload;
    const mixpanelPayload: IModalData = {
      nurseId:nurse._id, 
      nurseName: nurse.fullName, 
      triggerSource
    }
    const modalBody = this.generateModalText(nurse.name.first, 'invite-to-bs');
    const modalConfig = {
      modalTitle: `Invite ${nurse.name.first} to block schedule`,
      modalBody,
      okButtonText: 'Invite to block schedule',
      cancelButtonText: 'Don\'t show again for this nurse',
      color: 'primary',
      type: 'twoAction',
      titleSize: '18px',
      modalWidth: '548px'
    };
    this.openModalWithTracking(
      modalConfig,
      'INVITE_TO_BLOCK_SCHEDULE_MODAL_OPEN',
      'INVITE_TO_BLOCK_SCHEDULE_MODAL_CONFIRMED',
      'INVITE_TO_BLOCK_SCHEDULE_MODAL_DISMISSED',
      mixpanelPayload,
      () => this.router.navigate(['manager/shifts/block-schedule'], { state: { nurseId: nurse._id }}),
      () => this.nurseService.restrictPopupForNurse(nurse._id, facility, { showBlockSchedulePopup: false }).pipe(take(1)).subscribe({})
    );
  }
  showTrustedNursePopup(payload: IFacilityNurseEventPayload) {
    const { nurse, facility, triggerSource} = payload;
    const mixpanelPayload: IModalData = {
      nurseId:nurse._id, 
      nurseName: nurse.fullName, 
      triggerSource
    }
    const modalBody = this.generateModalText(nurse.name.first, 'mark-nurse-as-trusted');
    const modalConfig = {
      modalTitle: 'Mark as trusted',
      modalBody,
      okButtonText: 'Confirm nurse as trusted',
      cancelButtonText: 'Don\'t show again for this nurse',
      color: 'primary',
      type: 'twoAction',
      titleIcon: 'badgeCheck',
      titleSize: '18px',
      modalWidth: '554px'
    };
    this.openModalWithTracking(
      modalConfig, 
      'MARK_NURSE_AS_TRUSTED_MODAL_OPEN', 
      'MARK_NURSE_AS_TRUSTED_MODAL_CONFIRM', 
      'MARK_NURSE_AS_TRUSTED_MODAL_DISMISSED', 
      mixpanelPayload,
      () => {
        this.nurseFacade.markAsTrusted(facility, nurse._id).pipe(take(1), filter(r=>r.type === markAsTrustedSuccess.type)).subscribe(
          res =>  this.messagesService.add(`${nurse.name.first} marked as a trusted nurse`, 'Success', 'success')
        );
      }, 
      () => this.nurseService.restrictPopupForNurse(nurse._id, facility, { showTrustedPopup: false }).pipe(take(1)).subscribe({})
    );
  }
  private openModalWithTracking(
    modalConfig: any, 
    trackOpenEvent: TEventName,
    trackConfirmEvent: TEventName, 
    trackDismissEvent: TEventName, 
    modalData: IModalData,  
    confirmAction:()=>void, 
    dismissAction: ()=>void
  ) {
    this.mixpanel.for('fm').track(trackOpenEvent, {modalData});
    this.modalService.openModal(modalConfig).subscribe(result=>{
      if(result.action === 'firstAction'){
        this.mixpanel.for('fm').track(trackConfirmEvent, {modalData})
        confirmAction();
      }else if(result.action === 'secondAction'){
        this.mixpanel.for('fm').track(trackDismissEvent, {modalData});
        dismissAction();
      }
    })
  }
  private generateModalText(name: string, modal: ModalType): string {
    if (modal === 'invite-to-bs') {
      return `
        <div class="container text-primary">
            Seems like ${name} is working regularly at your facility. Why not invite her to block schedule shifts? 
            <div class="bs-description-list">
                <div class="description"> <img src="assets/custom-icons/check-circle.svg" /> Only ${name} will see invited shifts</div>
                <div class="description"> <img src="assets/custom-icons/check-circle.svg" /> Invited shifts expire in 12 hours</div>
                <div class="description"> <img src="assets/custom-icons/check-circle.svg" />Expired or rejected shifts can be re-posted for others</div>
                <div class="description"> <img src="assets/custom-icons/check-circle.svg" /> Send up to 90 shift invites with block schedule</div>
            </div>

            <div class="info-banner bs"><span class="bold">Did you know?</span> Block schedule is a great option to fill last minute needs. You can invite a nurse to a shift and she can view and accept the invited shift even after the shift starts.</div>
        </div>`;
    } else {
      return `<div class="text-primary"> Marking ${name} as trusted will make it easy for your team to recognize an ESHYFT nurse you trust</div> 
         <div class="info-banner">
            <span class="bold">Did you know?</span> 
            You can add trusted nurses for auto-confirmation during shift creation, so they can get confirmed for shift automatically.
        </div>`;
    }
  }
} 